import { useQuery } from "@tanstack/react-query";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { z } from "zod";
import { RegisterFlow } from "../../components/accreditation/register/register-flow";
import { usePageBar } from "../../components/shell/page-bar";
import { accreditationActionsOptions, accreditationRecordOptions } from "../../lib/api/accreditations";
import { ApiError } from "../../lib/api/client";
import { errorMessage } from "../../lib/api/messages";
import { toast } from "../../lib/ui/toast";

const STEPS = ["zone", "personal", "additional", "sport", "card", "review"] as const;

/**
 * Resume or edit one record in the register flow (`?step=`, the names the
 * record page's Edit links use). `created` shows the Personal checkpoint's
 * "Registration created" panel once, right after the DRAFT was made.
 *
 * Who may edit is `/actions.edit` — the same SQL the PATCH uses. A record the
 * viewer cannot change sends them back to it with her toast.
 */
export const Route = createFileRoute("/_app/accreditation/add/$id")({
	validateSearch: z.object({
		step: z.enum(STEPS).optional().catch(undefined),
		created: z.boolean().optional().catch(undefined),
	}),
	component: EditRecordPage,
});

function EditRecordPage() {
	const { t } = useTranslation("accreditation");
	const { id } = Route.useParams();
	const search = Route.useSearch();
	const { session } = Route.useRouteContext();
	const navigate = useNavigate();
	const record = useQuery(accreditationRecordOptions(id));
	const actions = useQuery(accreditationActionsOptions(id));
	// Her edit bar: "Edit record", the registration number under it, and a back arrow to the record.
	usePageBar({
		title: t("register.editTitle"),
		subtitle: record.data?.registration_no_display,
		back: { to: "/accreditation/records/$id", params: { id }, label: t("register.openRecord") },
	});

	// Decided once, on ENTRY: a record this viewer may not change sends them back to it. After the
	// user's own Submit in this page, `/actions` refetches with edit=false — that must not redirect
	// over the "Registration submitted" panel.
	const [entry, setEntry] = useState<{ id: string; locked: boolean } | null>(null);
	const entryLocked = entry?.id === id ? entry.locked : null;
	useEffect(() => {
		if (entryLocked === null && actions.data) setEntry({ id, locked: !actions.data.edit });
	}, [entryLocked, actions.data, id]);
	const locked = entryLocked === true;
	useEffect(() => {
		if (!locked) return;
		toast.info(t("register.toast.notEditable"));
		void navigate({ to: "/accreditation/records/$id", params: { id }, replace: true });
	}, [locked, id, navigate, t]);

	// A failed background refetch (after a save) keeps the page; only a failed first load is an error page.
	if ((record.isError && !record.data) || (actions.isError && entryLocked === null)) {
		const err = record.error ?? actions.error;
		const notFound = err instanceof ApiError && err.code === "NOT_FOUND";
		return (
			<div role="alert" className="mx-auto mt-[2rem] grid max-w-[32rem] justify-items-center gap-[0.5rem] rounded-[0.75rem] border border-gms-line bg-base-100 px-[1.25rem] py-[2rem] text-center">
				<p className="text-[0.9375rem] font-semibold text-gms-navy-ink">{notFound ? t("record.notFound") : t("record.loadFailed")}</p>
				{!notFound && <p className="text-[0.8125rem] text-gms-ink-2">{errorMessage(err)}</p>}
			</div>
		);
	}
	if (!record.data || entryLocked === null || locked) return <div className="skeleton h-[24rem] rounded-[0.75rem]" />;

	return (
		<RegisterFlow
			key={record.data.ac_card_id}
			me={session}
			mode={{ kind: "edit", record: record.data, created: !!search.created }}
			step={search.step ?? "review"}
			onStep={(s) => void navigate({ to: "/accreditation/add/$id", params: { id }, search: { step: s } })}
			onReload={async () => (await record.refetch()).data}
		/>
	);
}
